// The physics only uses Box2D state and deterministic maths; how the halves
// look is up to goreHalves, which may use Math.random.
import * as PIXI from 'pixi.js';
import { game } from '../../Game';
import { Settings } from '../../Settings';
import * as AudioManager from '../../utils/AudioManager';
import * as emitterManager from '../../utils/EmitterManager';
import { simulationTime } from '../../utils/SimulationClock';
import { recordReplayEvent } from '../../utils/ReplayExportJob';
import { HITSTOP_FRAMES, requestHitstop } from '../../utils/Hitstop';
import { area, clipHalf } from '../../utils/gore/goreGeometry';
import { bloodOf } from '../../utils/gore/goreArt';
import { cutArt, dressCut } from '../../utils/gore/goreHalves';
import { Humanoid } from '../humanoids/Humanoid';
import { attachFrame, cloneHalf, copyRevoluteJoint, frameLike } from './laserHalves';

const { getPointer, NULL } = Box2D;

const PARTS = ['head', 'body', 'belly', 'thigh_left', 'thigh_right', 'leg_left', 'leg_right', 'feet_left', 'feet_right',
	'shoulder_left', 'shoulder_right', 'arm_left', 'arm_right', 'hand_left', 'hand_right'];
const INSIDE = {head: 'skull', body: 'ribs', belly: 'guts'};
const LIMB = /^(thigh|leg|shoulder|arm)_/;
const MIN_PIECE_AREA = 0.02;
const MIN_BLADE_COVER = 0.5;
const COOLDOWN = 250;
const SEPARATION = 0.8;
const JOINT_REACH = 0.15;
const BITE = {part: 0.45, limb: 0.3};
const ACROSS_ONLY = /^(thigh|leg|shoulder|arm|feet)_/;
const ROUND = 2.5;
const BLADE_PULL = 0.25;
const BLADE_PULL_MAX = 0.26;
const ENTRY = 0.7;
const ALONG_BLADE = 0.7;
const ON_LINE = 1e-6;

const vec = new Box2D.b2Vec2(0, 0);

const local = (body, x, y) => {
	vec.Set(x, y);
	const p = body.GetLocalPoint(vec);
	return {x: p.x, y: p.y};
};
const world = (body, x, y) => {
	vec.Set(x, y);
	const p = body.GetWorldPoint(vec);
	return {x: p.x, y: p.y};
};
const worldVector = (body, x, y) => {
	vec.Set(x, y);
	const v = body.GetWorldVector(vec);
	return {x: v.x, y: v.y};
};
const localVector = (body, x, y) => {
	vec.Set(x, y);
	const v = body.GetLocalVector(vec);
	return {x: v.x, y: v.y};
};

const fixturesOf = body => {
	const fixtures = [];
	for (let fixture = body.GetFixtureList(); getPointer(fixture) !== getPointer(NULL); fixture = fixture.GetNext()) fixtures.push(fixture);
	return fixtures;
};

const jointsOf = body => {
	const joints = [];
	for (let edge = body.GetJointList(); getPointer(edge) !== getPointer(NULL); edge = edge.get_next()) joints.push(game.editor.CastJoint(edge.joint));
	return joints;
};

const polygonPoints = shape => {
	const polygon = Box2D.castObject(shape, Box2D.b2PolygonShape);
	const points = [];
	for (let i = 0; i < polygon.get_m_count(); i++) {
		const v = polygon.get_m_vertices(i);
		points.push({x: v.get_x(), y: v.get_y(), cut: -1});
	}
	return points;
};

const templateOf = fixture => {
	const filter = fixture.GetFilterData();
	return {
		density: fixture.GetDensity(), friction: fixture.GetFriction(), restitution: fixture.GetRestitution(), sensor: fixture.IsSensor(),
		category: filter.get_categoryBits(), mask: filter.get_maskBits(), group: filter.get_groupIndex(),
	};
};

const splitShapes = (body, line) => {
	const sides = {[-1]: [], [1]: []};
	for (const fixture of fixturesOf(body)) {
		const shape = fixture.GetShape();
		const template = templateOf(fixture);
		let points;
		if (shape.GetType() === Box2D.b2Shape.e_circle) {
			const circle = Box2D.castObject(shape, Box2D.b2CircleShape);
			const centre = circle.get_m_p();
			const x = centre.get_x();
			const y = centre.get_y();
			const r = circle.get_m_radius();
			const d = (x - line.px) * line.nx + (y - line.py) * line.ny;
			if (Math.abs(d) >= r) {
				sides[d > 0 ? -1 : 1].push({circle: {x, y, r}, template, area: Math.PI * r * r});
				continue;
			}
			points = [];
			for (let i = 0; i < 8; i++) points.push({x: x + r * Math.cos(i * Math.PI / 4), y: y + r * Math.sin(i * Math.PI / 4), cut: -1});
		} else if (shape.GetType() === Box2D.b2Shape.e_polygon) {
			points = polygonPoints(shape);
		} else {
			continue;
		}
		for (const side of [-1, 1]) {
			const half = clipHalf(points, line, side);
			if (half) sides[side].push({points: half, template, area: Math.abs(area(half))});
		}
	}
	return sides;
};

const addFixture = (body, shape, template) => {
	const fixtureDef = new Box2D.b2FixtureDef();
	fixtureDef.set_shape(shape);
	fixtureDef.set_density(template.density);
	fixtureDef.set_friction(template.friction);
	fixtureDef.set_restitution(template.restitution);
	fixtureDef.set_isSensor(template.sensor);
	const filter = fixtureDef.get_filter();
	filter.set_categoryBits(template.category);
	filter.set_maskBits(template.mask);
	filter.set_groupIndex(template.group);
	body.CreateFixture(fixtureDef);
	Box2D.destroy(fixtureDef);
};

// Box2D turns a degenerate polygon into a 2 m box, and takes at most 8 vertices.
const addPolygon = (body, points, template) => {
	const welded = [];
	for (const p of points) {
		const last = welded[welded.length - 1];
		if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 0.01) welded.push(p);
	}
	if (welded.length > 2 && Math.hypot(welded[0].x - welded[welded.length - 1].x, welded[0].y - welded[welded.length - 1].y) <= 0.01) welded.pop();
	if (welded.length < 3 || Math.abs(area(welded)) < 0.0004) return;
	if (welded.length > 8) {
		addPolygon(body, welded.slice(0, 8), template);
		addPolygon(body, [welded[0], ...welded.slice(7)], template);
		return;
	}
	const shape = new Box2D.b2PolygonShape();
	const [pointer, destroyVertices] = Box2D.pointsToVec2Array(welded);
	shape.Set(pointer, welded.length);
	destroyVertices();
	addFixture(body, shape, template);
	Box2D.destroy(shape);
};

const setShapes = (body, pieces) => {
	fixturesOf(body).forEach(fixture => body.DestroyFixture(fixture));
	for (const piece of pieces) {
		if (piece.circle) {
			const shape = new Box2D.b2CircleShape();
			shape.set_m_radius(piece.circle.r);
			vec.Set(piece.circle.x, piece.circle.y);
			shape.set_m_p(vec);
			addFixture(body, shape, piece.template);
			Box2D.destroy(shape);
		} else {
			addPolygon(body, piece.points, piece.template);
		}
	}
	body.ResetMassData();
};

const sideOf = (line, p) => (p.x - line.px) * line.nx + (p.y - line.py) * line.ny > 0 ? -1 : 1;

const jointAnchor = (joint, body) => {
	const anchor = joint.GetBodyA() === body ? joint.GetAnchorA() : joint.GetAnchorB();
	return local(body, anchor.x, anchor.y);
};

// Without trigonometry, so it stays deterministic.
const principalAxis = points => {
	let mx = 0, my = 0;
	for (const p of points) {
		mx += p.x / points.length;
		my += p.y / points.length;
	}
	let xx = 0, xy = 0, yy = 0;
	for (const p of points) {
		xx += (p.x - mx) * (p.x - mx);
		xy += (p.x - mx) * (p.y - my);
		yy += (p.y - my) * (p.y - my);
	}
	const spread = Math.sqrt((xx - yy) * (xx - yy) / 4 + xy * xy);
	const largest = (xx + yy) / 2 + spread;
	const smallest = (xx + yy) / 2 - spread;
	let ux = xy;
	let uy = largest - xx;
	if (Math.abs(ux) + Math.abs(uy) < 1e-12) {
		ux = xx >= yy ? 1 : 0;
		uy = xx >= yy ? 0 : 1;
	}
	const length = Math.sqrt(ux * ux + uy * uy);
	return {ux: ux / length, uy: uy / length, ratio: largest / Math.max(smallest, 1e-9)};
};

const bladeAxis = blade => {
	let best = null;
	for (const fixture of fixturesOf(blade)) {
		const shape = fixture.GetShape();
		if (shape.GetType() !== Box2D.b2Shape.e_polygon) continue;
		const points = polygonPoints(shape);
		const size = Math.abs(area(points));
		if (best && size <= best.size) continue;
		best = {size, points, ...principalAxis(points)};
	}
	return best;
};

const outlinePoints = body => {
	const points = [];
	for (const fixture of fixturesOf(body)) {
		const shape = fixture.GetShape();
		if (shape.GetType() === Box2D.b2Shape.e_circle) {
			const circle = Box2D.castObject(shape, Box2D.b2CircleShape);
			const centre = circle.get_m_p();
			const r = circle.get_m_radius();
			for (let i = 0; i < 8; i++) points.push({x: centre.get_x() + r * Math.cos(i * Math.PI / 4), y: centre.get_y() + r * Math.sin(i * Math.PI / 4)});
		} else if (shape.GetType() === Box2D.b2Shape.e_polygon) {
			points.push(...polygonPoints(shape));
		}
	}
	return points;
};

const cutAxesOf = (skin, part, outline) => {
	const shape = principalAxis(outline);
	if (ACROSS_ONLY.test(part)) return [{x: -shape.uy, y: shape.ux}];
	if (shape.ratio > ROUND) return [{x: shape.ux, y: shape.uy}, {x: -shape.uy, y: shape.ux}];
	const angle = skin.data?.textureAngleOffset || 0;
	return [{x: Math.cos(angle), y: -Math.sin(angle)}, {x: Math.sin(angle), y: Math.cos(angle)}];
};

// The blade can't sink into a part in the simulation, so its contact is always
// on the part's surface: a cut running alongside it is set into the part.
const cutLineFor = (skin, part, outline, blade, p, inward) => {
	let best = null;
	let bestDot = -1;
	for (const axis of cutAxesOf(skin, part, outline)) {
		const dot = Math.abs(axis.x * blade.ux + axis.y * blade.uy);
		if (dot > bestDot) {
			best = axis;
			bestDot = dot;
		}
	}
	const towards = best.x * blade.ux + best.y * blade.uy >= 0 ? 1 : -1;
	const turn = Math.atan2(best.x * blade.uy * towards - best.y * blade.ux * towards, bestDot);
	const angle = Math.max(-BLADE_PULL_MAX, Math.min(BLADE_PULL_MAX, turn * BLADE_PULL));
	const c = Math.cos(angle);
	const sn = Math.sin(angle);
	const ux = best.x * c - best.y * sn;
	const uy = best.x * sn + best.y * c;
	const entering = Math.abs(ux * inward.x + uy * inward.y) > ENTRY;
	let depth = 0;
	for (const v of outline) depth = Math.max(depth, (v.x - p.x) * inward.x + (v.y - p.y) * inward.y);
	const bite = entering ? 0 : ACROSS_ONLY.test(part) ? BITE.limb : BITE.part;
	const px = p.x + inward.x * depth * bite;
	const py = p.y + inward.y * depth * bite;
	return {line: {id: 0, px, py, dx: ux, dy: uy, nx: -uy, ny: ux}, alongBlade: Math.abs(ux * blade.ux + uy * blade.uy) > ALONG_BLADE};
};

const chordOf = (pieces, line) => {
	let min = Infinity;
	let max = -Infinity;
	for (const piece of pieces) {
		if (!piece.points) continue;
		for (const p of piece.points) {
			if (Math.abs((p.x - line.px) * line.nx + (p.y - line.py) * line.ny) > ON_LINE) continue;
			const t = (p.x - line.px) * line.dx + (p.y - line.py) * line.dy;
			min = Math.min(min, t);
			max = Math.max(max, t);
		}
	}
	return max > min ? {min, max} : null;
};

const gash = (body, line, chord) => {
	for (let i = 0; i < 4; i++) {
		const t = chord.min + (chord.max - chord.min) * (i + 0.5) / 4;
		game.editor.queueDecalToBody(body, world(body, line.px + line.dx * t, line.py + line.dy * t), 'Decal.png', true, 0.3);
	}
	const middle = (chord.min + chord.max) / 2;
	emitterManager.playOnceEmitter('blood', body, world(body, line.px + line.dx * middle, line.py + line.dy * middle));
};

const moveJoint = (joint, from, to, lookup) => {
	if (joint.GetType() !== Box2D.e_revoluteJoint) {
		game.editor.deleteObjects([joint]);
		return;
	}
	const names = Object.keys(lookup).filter(name => lookup[name] === joint);
	const copy = copyRevoluteJoint(joint, joint.GetBodyA() === from ? to : joint.GetBodyA(), joint.GetBodyB() === from ? to : joint.GetBodyB());
	game.editor.deleteObjects([joint]);
	if (copy) names.forEach(name => lookup[name] = copy);
};

const markSnapped = (far, kept) => {
	const seen = new Set([kept, far]);
	const queue = [far];
	while (queue.length) {
		const body = queue.shift();
		body.snapped = true;
		for (const joint of jointsOf(body)) {
			if (joint.GetType() !== Box2D.e_revoluteJoint) continue;
			const other = joint.GetBodyA() === body ? joint.GetBodyB() : joint.GetBodyA();
			if (seen.has(other) || !other.isHumanoid) continue;
			seen.add(other);
			queue.push(other);
		}
	}
};

// `hit` is the contact point, with its normal pointing from the blade into the part.
export const bladeCut = (blade, body, hit) => {
	if (!Settings.goreEnabled || body.destroyed || !body.isFlesh || body.depthCut || !body.myTexture || body.myTexture.destroyed) return false;
	if (!body.isHumanoid) {
		jointsOf(body).forEach(joint => !joint.destroyed && game.editor.deleteObjects([joint]));
		return false;
	}
	const humanoid = game.editor.retrieveSubClassFromBody(body);
	if (!humanoid?.lookupObject || humanoid.invincible || game.tutorialMode || humanoid.isPortalTransitActive?.()) return false;
	const skin = body.myTexture;
	const now = simulationTime();
	if (now - (skin.goreCutTime ?? -Infinity) < (hit.cooldown ?? COOLDOWN)) return false;
	const part = skin.goreCut?.part || body.mySprite.data.refName;
	if (!PARTS.includes(part)) return false;
	// A round blade has no long edge, so it passes the line it cuts along.
	const axis = hit.axis || bladeAxis(blade);
	if (!axis) return false;

	const along = worldVector(blade, axis.ux, axis.uy);
	const p = local(body, hit.x, hit.y);
	const d = localVector(body, along.x, along.y);
	const length = Math.hypot(d.x, d.y) || 1;
	let inward = hit.nx === undefined ? null : localVector(body, hit.nx, hit.ny);
	if (!inward) {
		const centre = body.GetLocalCenter();
		inward = {x: centre.x - p.x, y: centre.y - p.y};
	}
	const inwardLength = Math.hypot(inward.x, inward.y) || 1;
	const {line, alongBlade} = cutLineFor(skin, part, outlinePoints(body), {ux: d.x / length, uy: d.y / length}, p, {x: inward.x / inwardLength, y: inward.y / inwardLength});
	const sides = splitShapes(body, line);
	const areas = {[-1]: sides[-1].reduce((sum, piece) => sum + piece.area, 0), [1]: sides[1].reduce((sum, piece) => sum + piece.area, 0)};
	const chord = chordOf([...sides[-1], ...sides[1]], line);
	if (!chord) return false;
	skin.goreCutTime = now;

	const lookup = humanoid.lookupObject;
	if (Math.min(areas[-1], areas[1]) < MIN_PIECE_AREA) {
		// A sliver: a joint in it gives way instead.
		const small = areas[-1] < areas[1] ? -1 : 1;
		let name = null;
		let deepest = -JOINT_REACH;
		for (const key of Object.keys(lookup)) {
			const joint = lookup[key];
			if (!key.endsWith('_joint') || !joint || joint.destroyed || (joint.GetBodyA() !== body && joint.GetBodyB() !== body)) continue;
			const anchor = jointAnchor(joint, body);
			const depth = -small * ((anchor.x - line.px) * line.nx + (anchor.y - line.py) * line.ny);
			if (depth > deepest) {
				deepest = depth;
				name = key;
			}
		}
		if (name) humanoid.collisionUpdates.push({type: Humanoid.GORE_SNAP, target: name.slice(0, -'_joint'.length)});
		else gash(body, line, chord);
		return false;
	}

	let reachMin = Infinity;
	let reachMax = -Infinity;
	for (const v of axis.points) {
		const w = world(blade, v.x, v.y);
		const q = local(body, w.x, w.y);
		const t = (q.x - line.px) * line.dx + (q.y - line.py) * line.dy;
		reachMin = Math.min(reachMin, t);
		reachMax = Math.max(reachMax, t);
	}
	const covered = Math.min(chord.max, reachMax) - Math.max(chord.min, reachMin);
	if (alongBlade && covered < (chord.max - chord.min) * MIN_BLADE_COVER) {
		gash(body, line, {min: Math.max(chord.min, reachMin), max: Math.min(chord.max, reachMax)});
		return false;
	}

	const root = lookup[part === 'body' ? 'belly_joint' : `${part}_joint`];
	const keptSide = root && !root.destroyed && (root.GetBodyA() === body || root.GetBodyB() === body)
		? sideOf(line, jointAnchor(root, body))
		: areas[-1] >= areas[1] ? -1 : 1;
	const farSide = -keptSide;
	const art = cutArt(skin, LIMB.test(part));

	const far = cloneHalf(body, true);
	far.isFlesh = true;
	far.isHumanoid = true;
	far.bloodSkin = body.bloodSkin;
	far.noDamage = true;
	setShapes(far, sides[farSide]);
	setShapes(body, sides[keptSide]);
	const pointVelocity = body.GetLinearVelocityFromWorldPoint(far.GetWorldCenter());
	const velocity = {x: pointVelocity.x, y: pointVelocity.y};
	const away = worldVector(body, line.nx * -farSide, line.ny * -farSide);
	vec.Set(velocity.x + away.x * SEPARATION, velocity.y + away.y * SEPARATION);
	far.SetLinearVelocity(vec);
	far.SetAngularVelocity(body.GetAngularVelocity());

	for (const joint of jointsOf(body)) {
		if (joint.destroyed || sideOf(line, jointAnchor(joint, body)) !== farSide) continue;
		moveJoint(joint, body, far, lookup);
	}
	markSnapped(far, body);

	const farSkin = frameLike(skin);
	const textures = game.editor.textures;
	textures.addChildAt(farSkin, textures.getChildIndex(skin));
	attachFrame(far, farSkin, skin);
	far.myRTCache = body.myRTCache;
	far.myDecalEntry = null;
	far.decalLookupKey = body.decalLookupKey;
	const keptCentre = body.GetLocalCenter();
	const gravity = game.world.GetGravity();
	const gravityLength = Math.hypot(gravity.x, gravity.y);
	const up = gravityLength ? localVector(body, -gravity.x / gravityLength, -gravity.y / gravityLength) : localVector(body, 0, -1);
	const dressed = art && dressCut({
		skin, farSkin, art, up, line, keptPoint: {x: keptCentre.x, y: keptCentre.y},
		inside: INSIDE[part], organs: !humanoid.noGoreParticles, blood: bloodOf(body),
	});
	if (!dressed) {
		for (const child of [skin.myFlesh, skin.originalSprite]) {
			if (child && !child.destroyed) farSkin.addChild(new PIXI.Sprite(child.texture));
		}
	}
	if (farSkin.goreCut) farSkin.goreCut.part = part;
	if (skin.goreCut) skin.goreCut.part = part;
	farSkin.goreCutTime = now;

	const middle = (chord.min + chord.max) / 2;
	const anchor = {x: line.px + line.dx * middle, y: line.py + line.dy * middle};
	const point = world(body, anchor.x, anchor.y);
	const cutAlong = {x: line.dx, y: line.dy, length: chord.max - chord.min};
	humanoid.bloodSprays.push({body, anchor: {...anchor}, along: cutAlong, angle: 0, time: 6500}, {body: far, anchor: {...anchor}, along: cutAlong, angle: 0, time: 6500, strength: 0.55});
	for (let i = 0; i < 3; i++) {
		const t = chord.min + (chord.max - chord.min) * (i + 0.5) / 3;
		game.editor.queueDecalToBody(body, world(body, line.px + line.dx * t + line.nx * keptSide * -0.1, line.py + line.dy * t + line.ny * keptSide * -0.1), 'Decal.png', false, 0.45);
	}
	emitterManager.playOnceEmitter('blood', body, point);
	AudioManager.playSFX('sword-slash', 0.5, 0.9 + Math.random() * 0.2, point);
	if (part === 'head') {
		humanoid.dropMask?.();
		humanoid.hat?.detach();
	}
	if ((part === 'head' || part === 'body') && humanoid.bleedTimer < 0) humanoid.bleedTimer = 0;
	humanoid.checkLimbs?.();
	recordReplayEvent('slice', {who: humanoid.replayActor(), part}, point);
	requestHitstop(part === 'head' || part === 'body' ? HITSTOP_FRAMES.HEAD : HITSTOP_FRAMES.LIMB, point, {victim: humanoid.lookupObject._bodies});
	return true;
};
